import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { queryKeys } from '~/api/queryKeys';

import { getDefaultGroupingConfig } from '../services/grouping-config.service';
import { fetchProductColumns } from '../services/product-metadata.service';
import type { ContentAttribute } from '../types/create-experiment.types';
import { platformForConnector } from '../utils/platform-mapping';

import { useContentAttributes, useKpis } from './use-config';
import { useProductColumns } from './use-product-metadata';

interface UseResumePrerequisitesArgs {
  /** Resolved channel — `null` only when no fallback connection could be resolved either. */
  channel: { connectionId: string; code: string } | null;
  /** Wizard step the user will land on after hydration. */
  targetStep: 1 | 2 | 3 | 4 | 5;
  /**
   * Whether a resume is actually in progress (`?expId=` present). `false` for
   * the fresh-create flow — keeps the always-blocking prereqs (notably the
   * content-attribute catalog, which is otherwise channel-agnostic and would
   * fire eagerly) from running when there's no draft to hydrate.
   */
  enabled: boolean;
}

interface UseResumePrerequisitesResult {
  /** All blocking prereqs have settled successfully — safe to hydrate + render the wizard. */
  isReady: boolean;
  /** First error from any blocking prereq, surfaced to the loader for toast/redirect. */
  error: Error | null;
  /** Content-attribute catalog — required by `hydrateFromDraft` to inflate Step 4's nested shape. */
  contentAttributes: ContentAttribute[] | undefined;
}

/**
 * Coordinate the catalog fetches needed to resume a draft. Two phases:
 *
 *   1. Blocking: catalogs needed to (a) hydrate correctly regardless of step
 *      (content-attribute catalog) and (b) render the target step's primary UI
 *      (kpis when landing on Step 4 or 5).
 *   2. Background: everything earlier steps will need on back-nav (columns,
 *      grouping-config). Prefetched via `queryClient.prefetchQuery` so they
 *      don't gate the overlay but warm the cache.
 */
export function useResumePrerequisites(
  args: UseResumePrerequisitesArgs,
): UseResumePrerequisitesResult {
  const { channel, targetStep, enabled } = args;
  const queryClient = useQueryClient();

  const connectorCode = channel?.code ?? null;
  const platform = platformForConnector(connectorCode);

  // -- Blocking ---------------------------------------------------------

  // Content-attribute catalog is blocking when resuming — needed to inflate
  // the wire's `string[]` of selected sub-ids back to the nested store shape.
  // Gated on `enabled` so a fresh-create run doesn't fire it eagerly (Step 4
  // fetches it on its own when reached).
  const contentAttributesQuery = useContentAttributes(enabled);

  // KPIs block only when landing on Step 4 (user-input) or Step 5 (review
  // labels). Earlier steps don't read KPIs; we still warm the cache below.
  const needsKpis = targetStep >= 4;
  const kpisQuery = useKpis(needsKpis ? platform : null);

  // -- Background warm --------------------------------------------------

  // Columns are needed by Step 2 / Step 3 only; warmed for back-nav.
  useProductColumns(targetStep > 3 ? connectorCode : null);

  // Grouping config (chat panel options) is only needed at Step 1; warm
  // for back-nav from any later step.
  useEffect(() => {
    if (!connectorCode || targetStep === 1) return;
    void queryClient.prefetchQuery({
      queryKey: queryKeys.connectors.defaultGroupingConfig(connectorCode),
      queryFn: () => getDefaultGroupingConfig(connectorCode),
      staleTime: 10 * 60 * 1000,
    });
  }, [queryClient, connectorCode, targetStep]);

  // Columns prefetch for back-nav from Step 4/5 — useProductColumns above is
  // gated on `targetStep > 3`; for steps 2/3 it already runs. For step 4/5 we
  // additionally seed via prefetchQuery to avoid an extra blocking subscription.
  useEffect(() => {
    if (!connectorCode || targetStep < 4) return;
    void queryClient.prefetchQuery({
      queryKey: queryKeys.products.columns(connectorCode),
      queryFn: () => fetchProductColumns(connectorCode),
      staleTime: 10 * 60 * 1000,
    });
  }, [queryClient, connectorCode, targetStep]);

  // -- Aggregate readiness ---------------------------------------------

  const blockingError =
    contentAttributesQuery.error ?? (needsKpis ? (kpisQuery.error ?? null) : null) ?? null;

  const isReady = contentAttributesQuery.isSuccess && (!needsKpis || kpisQuery.isSuccess);

  return {
    isReady,
    error: blockingError,
    contentAttributes: contentAttributesQuery.data,
  };
}
